import { useState, useEffect, useRef } from "react";
import adminFetch from "../../utils/adminFetch";
import { getTokenPayload } from "../ProtectedRoute";

const UNASSIGNED = { userId: "", username: "", name: "" };

export default function AssignmentDropdown({ entityType, entityId, assignedTo, onAssigned, isMock }) {
  const [agents,   setAgents]   = useState([]);
  const [open,     setOpen]     = useState(false);
  const [saving,   setSaving]   = useState(false);
  const ref = useRef(null);
  const current = getTokenPayload();

  // Load admin users once on mount
  useEffect(() => {
    adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/users`)
      .then(r => r.json())
      .then(d => { if (d.success) setAgents(d.users); })
      .catch(() => {});
  }, []);

  // Close on outside click
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const assign = async (agent) => {
    if (isMock) { setOpen(false); return; }
    setSaving(true);
    try {
      const res  = await adminFetch(
        `${process.env.REACT_APP_API_URL}/api/admin/${entityType}/${entityId}/assign`,
        { method: "PATCH", body: JSON.stringify(agent) }
      );
      const data = await res.json();
      if (data.success) onAssigned(data.assignedTo);
    } catch (_) {}
    setSaving(false);
    setOpen(false);
  };

  const isAssigned  = !!assignedTo?.userId;
  const displayName = isAssigned ? assignedTo.name || assignedTo.username : "Unassigned";
  const isMe        = assignedTo?.userId === current?.userId;

  return (
    <div>
      <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
        Assigned To
      </p>

      <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
        <button
          onClick={() => !isMock && setOpen(v => !v)}
          disabled={saving}
          style={{
            display: "flex", alignItems: "center", gap: "8px",
            fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.12em",
            textTransform: "uppercase", cursor: isMock ? "default" : "pointer",
            background: isAssigned ? "rgba(1,46,114,0.06)" : "rgba(1,46,114,0.03)",
            border: `1px solid ${isAssigned ? "rgba(1,46,114,0.18)" : "rgba(1,46,114,0.09)"}`,
            borderRadius: "10px", padding: "8px 14px", color: isAssigned ? "#012E72" : "#94a3b8",
            transition: "all 0.15s",
          }}
          onMouseEnter={e => { if (!isMock) e.currentTarget.style.background = "rgba(1,46,114,0.09)"; }}
          onMouseLeave={e => { e.currentTarget.style.background = isAssigned ? "rgba(1,46,114,0.06)" : "rgba(1,46,114,0.03)"; }}
        >
          {/* Avatar circle */}
          <div style={{
            width: "22px", height: "22px", borderRadius: "50%", flexShrink: 0,
            background: isAssigned ? "linear-gradient(135deg,#012E72,#002DB5)" : "rgba(1,46,114,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {isAssigned ? (
              <span style={{ fontFamily: "Arial", fontSize: "9px", color: "#fff", fontWeight: 700 }}>
                {(assignedTo.name || assignedTo.username || "?")[0].toUpperCase()}
              </span>
            ) : (
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
            )}
          </div>

          <span>{displayName}{isMe ? " (me)" : ""}</span>

          {!isMock && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          )}
        </button>

        {/* Dropdown */}
        {open && (
          <div style={{
            position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 60,
            background: "#fff", borderRadius: "14px", minWidth: "220px",
            border: "1px solid rgba(1,46,114,0.1)",
            boxShadow: "0 12px 40px rgba(1,46,114,0.14), 0 2px 8px rgba(0,0,0,0.06)",
            overflow: "hidden",
          }}>
            {/* Unassign option */}
            <button
              onClick={() => assign(UNASSIGNED)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "10px",
                padding: "10px 14px", background: "none", border: "none", cursor: "pointer",
                borderBottom: "1px solid #f1f5f9", textAlign: "left",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f7f9ff"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
              </div>
              <span style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.1em", color: "#94a3b8" }}>Unassigned</span>
            </button>

            {/* Assign to me shortcut */}
            <button
              onClick={() => assign({ userId: current?.userId, username: current?.username, name: current?.name })}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: "10px",
                padding: "10px 14px", background: "none", border: "none", cursor: "pointer",
                borderBottom: agents.length > 0 ? "1px solid #f1f5f9" : "none", textAlign: "left",
              }}
              onMouseEnter={e => e.currentTarget.style.background = "#f7f9ff"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "linear-gradient(135deg,#012E72,#002DB5)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontFamily: "Arial", fontSize: "11px", color: "#fff", fontWeight: 700 }}>
                  {(current?.name || current?.username || "?")[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.1em", color: "#012E72", marginBottom: "1px" }}>{current?.name || current?.username}</p>
                <p style={{ fontFamily: "'Constantia', serif", fontSize: "10px", color: "#94a3b8" }}>Assign to me</p>
              </div>
            </button>

            {/* Other agents */}
            {agents.filter(a => a._id !== current?.userId && a.active).map(agent => (
              <button
                key={agent._id}
                onClick={() => assign({ userId: agent._id, username: agent.username, name: agent.name })}
                style={{
                  width: "100%", display: "flex", alignItems: "center", gap: "10px",
                  padding: "10px 14px", background: "none", border: "none", cursor: "pointer",
                  borderBottom: "1px solid #f8fafc", textAlign: "left",
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f7f9ff"}
                onMouseLeave={e => e.currentTarget.style.background = "none"}
              >
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "rgba(1,46,114,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <span style={{ fontFamily: "Arial", fontSize: "11px", color: "#012E72", fontWeight: 700 }}>
                    {(agent.name || agent.username || "?")[0].toUpperCase()}
                  </span>
                </div>
                <p style={{ fontFamily: "'Cinzel', serif", fontSize: "10px", letterSpacing: "0.1em", color: "#012E72" }}>{agent.name || agent.username}</p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
